import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef } from 'react';
import type { ImageGrid5Props, ImageGrid5Ref } from './ImageGrid5.data';
import styles from './ImageGrid5.module.scss';
import {
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
  imageShiftTimeline,
} from './ImageGrid5.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import useScrollTrigger from '@/hooks/useScrollTrigger';
import Image from 'next/image';
import useSetupTimeline from '@/hooks/useSetupTimeline';

export default function ImageGrid5({
  isVisible,
  className,
  blok,
  ref,
}: ImageGrid5Props & { ref?: Ref<ImageGrid5Ref> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement>;
  const imageItemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const imageRefs = useRef<Array<HTMLImageElement | null>>([]);

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  const { inView } = useScrollTrigger({
    trigger: elementRef,
    enableInView: true,
  });

  useChangeTransitionState(transitionController, {
    onMount: isVisible?.onMount ?? true,
    value: (isVisible?.value ?? true) && !!inView,
  });

  useScrollTrigger({
    // trigger: { current: imageItemRefs.current[0] },
    // endTrigger: elementRef,
    trigger: elementRef,
    animation: useSetupTimeline(imageShiftTimeline, {
      imageItemRefs,
    }),
    vars: {
      scrub: true,
      start: 'center bottom',
      end: 'center top',
    },
  });

  return (
    <div
      ref={elementRef}
      className={classNames(styles.imageGrid5, className)}
      {...sbEditable(blok)}
    >
      {blok.images.slice(0, 5).map((image, index) => (
        <div
          key={image.id}
          ref={(element) => {
            imageItemRefs.current[index] = element;
          }}
          className={styles.imageItem}
        >
          <Image
            ref={(element) => {
              imageRefs.current[index] = element;
            }}
            src={image.filename}
            alt={image.alt || ''}
            className={styles.image}
            fill
            sizes="33vw"
          />
        </div>
      ))}
    </div>
  );
}
